// https://confluence.sephora.com/wiki/spaces/ILLUMINATE/pages/558304849/BI+Phone+-+Phase+2+-+Quick+Links+and+Documentation#BIPhonePhase2QuickLinksandDocumentation-AuthService

import ufeApi from 'services/api/ufeApi';

/**
 * Shared success/error gate for the auth (validateUser / verifyOTP) responses.
 * The backend signals failure three ways — an `errors` array, an `errorCode`
 * field, or an HTTP `responseStatus >= 400` — and all three MFA calls handle
 * them identically. Returns the response on success; returns a rejected promise
 * carrying the backend error on failure. Kept as a `.then` handler (not
 * async/await) so callers return the ufeApi promise directly per the UI API
 * convention.
 * @param {Object} response - Raw response from ufeApi.makeRequest.
 * @returns {Object|Promise} - The response, or a rejected promise on error.
 */
function rejectOnErrorEnvelope(response) {
    if (response?.errors) {
        return Promise.reject(response.errors[0]);
    }

    if (response?.errorCode) {
        return Promise.reject(response);
    }

    if (response?.responseStatus >= 400) {
        return Promise.reject(response);
    }

    return response;
}

/**
 * Validates MFA (phone or email) against backend service
 * @param {string} phone - Optional, mandatory for mfaMode=phone
 * @param {string} email - Mandatory
 * @param {string} mfaMode - Mandatory. Possible values: "phone", "email"
 * @returns {Promise} - Resolves with response data, rejects with error details
 */
function validateMFAEmailPhone(phone, email, mfaMode) {
    const payload = {
        email,
        mfaMode,
        ...(phone && { phone })
    };

    return ufeApi
        .makeRequest(
            '/gway/v1/dotcom/auth/v1/validateUser',
            {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'Content-type': 'application/json',
                    'x-requested-source': 'web',
                    'x-api-version': 1
                }
            },
            {
                // Skip token refresh during MFA to avoid corrupting session state
                skipTokenCheck: true
            }
        )
        .then(rejectOnErrorEnvelope);
}

/**
 * Send (trigger) an OTP for the new /signin flow (SIM-7 / ACS-4131, ADR-SIM7-002).
 *
 * Posts the NEW-flow body { securityToken, mfaMode, requestOrigin: 'OTPSignIn' } to
 * the triggerOTP endpoint (/validateUser). Unlike the legacy validateMFAEmailPhone,
 * the new flow is keyed by securityToken (from the authenticationMethods lookup) and
 * carries NO raw phone/email and NO otpId (ADR-SIM7-001 — no PII in the body, BR-021).
 * Reuses the same skipTokenCheck + error-envelope handling as mfaVerifyOTP.
 *
 * @param {string} securityToken - Intermediate token from authenticationMethods.
 * @param {string} mfaMode - 'phone' | 'email' (OTP delivery mode).
 * @returns {Promise} - Resolves with the response, rejects with the backend error.
 */
function mfaSendOtp(securityToken, mfaMode) {
    const payload = {
        securityToken,
        mfaMode,
        requestOrigin: 'OTPSignIn'
    };

    return ufeApi
        .makeRequest(
            '/gway/v1/dotcom/auth/v1/validateUser',
            {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'Content-type': 'application/json',
                    'x-requested-source': 'web',
                    'x-api-version': 1
                }
            },
            {
                // Skip token refresh during OTP send to avoid corrupting session state.
                skipTokenCheck: true
            }
        )
        .then(rejectOnErrorEnvelope);
}

/**
 * Validates OTP against backend service
 * @param {string} email - Optional. Raw entered email for email customers; OMITTED for
 *   phone customers in the new /signin flow (F-001 / ADR-SIM7-001) — the verify body is
 *   keyed by securityToken and must never carry a masked/placeholder email (BR-021).
 *   Legacy MFA callers still pass a raw email, so their request stays byte-identical.
 * @param {string} otpCode - Mandatory - OPT code user provided on FE
 * @param {string} mfaMode - Mandatory. Possible values: "phone", "email"
 * @param {string} keepSignedIn - // Optional - will be used to generate JWT tokens, should be true if user selected "Stay signed in" during login
 * @param {Object} [options] - New /signin flow (SIM-10) enhancements, added ONLY when present:
 * @param {string} [options.securityToken] - Intermediate token from authenticationMethods (new OTP sign-in). Legacy MFA omits it.
 * @param {string} [options.requestOrigin] - e.g. "OTPSignIn"; distinguishes the new flow server-side.
 * @returns {Promise}
 */
function mfaVerifyOTP(email, otpCode, mfaMode, keepSignedIn, options = {}) {
    const { securityToken, requestOrigin } = options;

    const payload = {
        // F-001: only include a raw email when one exists (email customers). A phone
        // customer has no raw email; the request is keyed by securityToken instead.
        ...(email && { email }),
        otpCode,
        mfaMode,
        ...(keepSignedIn && { keepSignedIn }),
        // Additive for the new /signin flow; absent for legacy MFA so the request is byte-identical.
        ...(securityToken && { securityToken }),
        ...(requestOrigin && { requestOrigin })
    };

    return ufeApi
        .makeRequest(
            '/gway/v1/dotcom/auth/v1/verifyOTP',
            {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'Content-type': 'application/json',
                    'x-requested-source': 'web',
                    'x-api-version': 1
                }
            },
            {
                // verifyOTP is an auth endpoint that creates tokens, not consumes them.
                // Without this flag, makeRequest detects no sis cookie (user is mid-MFA)
                // and triggers an anonymous refresh BEFORE verifyOTP fires, which can
                // corrupt the MFA session state and cause the subsequent getUserFull to 401.
                skipTokenCheck: true
            }
        )
        .then(rejectOnErrorEnvelope);
}

export default {
    validateMFAEmailPhone,
    mfaSendOtp,
    mfaVerifyOTP
};
